import React, { useState } from 'react';
import { useAuth, usePermissions } from '../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import OfflineIndicator from './OfflineIndicator';
import {
  HomeIcon,
  CubeIcon,
  QrCodeIcon,
  TagIcon,
  ChartBarIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  WifiIcon,
  SignalSlashIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  ShoppingCartIcon,         // (2026-04-17, Jason Cox) Manual Checkout nav item
  ArchiveBoxIcon,           // (2026-04-24, Jason Cox) Inventory nav item
  SignalIcon,               // (2026-04-22, Jason Cox) Device Status nav item
  DocumentTextIcon,         // (2026-04-22, Jason Cox) Consumption Events nav item
  TruckIcon,                // (2026-04-23, Jason Cox) Restock Events nav item
} from '@heroicons/react/24/outline';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isLoggingOut } = useAuth();
  const permissions = usePermissions();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon, current: location.pathname === '/' },
    { 
      name: 'Tag Items', 
      href: '/tag-items', 
      icon: QrCodeIcon, 
      current: location.pathname === '/tag-items',
      show: permissions.canMatchTags
    },
    {
      name: 'Manage Tags',
      href: '/manage-tags',
      icon: TagIcon,
      current: location.pathname === '/manage-tags',
      show: permissions.canManageTags
    },
    // (2026-04-17, Jason Cox) Manual Checkout -- hand-scanner driven. Smart-only:
    // gated on user.system === 'smart' plus the canManualCheckout permission
    // (which is hard-false for SWS users in AuthContext).
    {
      name: 'Manual Checkout',
      href: '/manual-checkout',
      icon: ShoppingCartIcon,
      current: location.pathname === '/manual-checkout',
      show: user?.system === 'smart' && (permissions.canManualCheckout ?? false)
    },
    // (2026-04-24, Jason Cox) Inventory -- Smart-only, aggregated on-hand
    // tags per SmartBox.
    {
      name: 'Inventory',
      href: '/inventory',
      icon: ArchiveBoxIcon,
      current: location.pathname === '/inventory',
      show: user?.system === 'smart' && (permissions.canViewInventory ?? false)
    },
    // (2026-04-22, Jason Cox) Device Status -- Smart-only, read-only view of
    // smartbox online/offline/empty state.
    {
      name: 'Device Status',
      href: '/device-status',
      icon: SignalIcon,
      current: location.pathname === '/device-status',
      show: user?.system === 'smart' && (permissions.canViewDeviceStatus ?? false)
    },
    // (2026-04-22, Jason Cox) Consumption Events (delivery tickets).
    {
      name: 'Consumption Events',
      href: '/delivery-tickets',
      icon: DocumentTextIcon,
      current: location.pathname === '/delivery-tickets',
      show: user?.system === 'smart' && (permissions.canViewDeliveryTickets ?? false)
    },
    // (2026-04-23, Jason Cox) Restock Events (transfer tickets).
    {
      name: 'Restock Events',
      href: '/restock-events',
      icon: TruckIcon,
      current: location.pathname === '/restock-events',
      show: user?.system === 'smart' && (permissions.canViewRestockEvents ?? false)
    },
  ].filter(item => item.show !== false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    // (2026-04-30) iOS Safari fix: min-h-dvh tracks the *visible* viewport
    // (collapsing URL bar + home indicator) instead of the static 100vh
    // that min-h-screen resolves to. The previous overflow-hidden /
    // overflow-y-auto pair never actually clipped because no parent had
    // a fixed height, but on iPhone it interacted with the dynamic
    // viewport to chop the bottom of the page off. Now the document
    // scrolls naturally and the safe-area padding keeps the last bits
    // of content above the home indicator.
    <div className="min-h-dvh bg-secondary-50">
      <div className="flex">
        {/* Mobile sidebar */}
        <div className={`fixed inset-0 z-40 flex md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex flex-col flex-1 max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            <SidebarContent navigation={navigation} user={user} onLogout={handleLogout} isLoggingOut={isLoggingOut} />
          </div>
        </div>

        {/* Desktop sidebar */}
        <div className="hidden md:flex md:flex-shrink-0">
          <div className="flex flex-col w-64">
            <SidebarContent navigation={navigation} user={user} onLogout={handleLogout} isLoggingOut={isLoggingOut} />
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-col flex-1">
          <header className="bg-white shadow-sm border-b border-secondary-200">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-20">
                <div className="flex items-center min-w-0">
                  <button
                    type="button"
                    className="md:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-secondary-500 hover:text-secondary-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                    onClick={() => setSidebarOpen(true)}
                  >
                    <Bars3Icon className="h-6 w-6" />
                  </button>
                  {user?.firstName && (
                    <h2 className="ml-2 md:ml-0 text-lg md:text-2xl font-bold text-secondary-900 truncate">
                      Welcome back, {user.firstName}
                    </h2>
                  )}
                </div>

                <div className="flex items-center space-x-4">
                  <OfflineIndicator />
                  <UserProfile user={user} onLogout={handleLogout} isLoggingOut={isLoggingOut} />
                </div>
              </div>
            </div>
          </header>

          <main
            className="flex-1 pb-[env(safe-area-inset-bottom)]"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

const SidebarContent: React.FC<{
  navigation: any[];
  user: any;
  onLogout: () => void;
  isLoggingOut?: boolean;
}> = ({ navigation, user, onLogout, isLoggingOut }) => {
  return (
    <div className="flex flex-col h-full bg-white border-r border-secondary-200">
      <div className="flex items-center justify-center h-20 flex-shrink-0 px-2 bg-white border-b border-secondary-200">
        <img src={`${process.env.PUBLIC_URL}/Rebound_Logo.png`} alt="Rebound Dynamics Logo" className="h-14 w-auto" />
      </div>

      <nav className="flex-1 px-2 py-4 space-y-1">
        {navigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`${
                item.current
                  ? 'bg-primary-100 text-primary-900 border-r-2 border-primary-600'
                  : 'text-secondary-700 hover:bg-secondary-100 hover:text-secondary-900'
              } group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors`}
            >
              <Icon className="h-5 w-5 mr-3" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      <div className="flex-shrink-0 p-4 border-t border-secondary-200">
        <div className="flex items-center">
          <UserCircleIcon className="h-10 w-10 text-secondary-400" />
          <div className="ml-3">
            <p className="text-sm font-medium text-secondary-900">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-secondary-500 capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={async () => {
            const feedback = Sentry.getFeedback();
            if (feedback) {
              const form = await feedback.createForm();
              form.appendToDom();
              form.open();
            }
          }}
          className="mt-3 w-full flex items-center px-3 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-100 hover:text-secondary-900 rounded-md transition-colors"
        >
          <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3" />
          Report an Issue
        </button>
        <button
          onClick={onLogout}
          disabled={isLoggingOut}
          className="w-full flex items-center px-3 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-100 hover:text-secondary-900 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoggingOut ? (
            <ArrowPathIcon className="h-5 w-5 mr-3 animate-spin" />
          ) : (
            <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
          )}
          {isLoggingOut ? 'Signing out...' : 'Sign out'}
        </button>
      </div>
    </div>
  );
};

const UserProfile: React.FC<{
  user: any;
  onLogout: () => void;
  isLoggingOut?: boolean;
}> = ({ user, onLogout, isLoggingOut }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center max-w-xs text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <UserCircleIcon className="h-8 w-8 text-secondary-400" />
        <span className="ml-2 text-secondary-700 hidden sm:block">
          {user?.firstName} {user?.lastName}
        </span>
      </button>

      {dropdownOpen && (
        <div className="origin-top-right absolute right-0 mt-2 min-w-56 max-w-xs rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="py-1">
            <div className="px-4 py-2 text-sm text-secondary-700 border-b border-secondary-200">
              <p className="font-medium">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-secondary-500 break-words">{user?.username}</p>
            </div>
            <button
              onClick={async () => {
                const feedback = Sentry.getFeedback();
                if (feedback) {
                  const form = await feedback.createForm();
                  form.appendToDom();
                  form.open();
                }
                setDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100 transition-colors flex items-center"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
              Report an Issue
            </button>
            <button
              onClick={onLogout}
              disabled={isLoggingOut}
              className="w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoggingOut && <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />}
              {isLoggingOut ? 'Signing out...' : 'Sign out'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


export default Layout;